import { Base64 } from 'js-base64';
import type { LoggedInUser } from '@/api/types';

// Wraps localStorage / sessionStorage with prefixed keys so AgentStudio.Web's
// session never collides with Tenant.Web / Member.Web / Merchant.Web running
// in the same browser. Deliberately mirrors Tenant.Web's SessionHelper API
// shape so the auth flow ports over easily.

const PREFIX = 'agent_studio_';
const KEYS = {
  rememberMe: `${PREFIX}remember_me`,
  token: `${PREFIX}access_token`,
  refresh: `${PREFIX}refresh_token`,
  user: `${PREFIX}user_info`,
  selectedMerchantId: `${PREFIX}selected_merchant_id`,
  // Path the user was on when their session was forced to /login.
  // Lives in sessionStorage only — automatically gone after the tab
  // closes — so a stale returnTo can't haunt a future cold sign-in.
  returnTo: `${PREFIX}return_to`,
} as const;

/**
 * 5 minutes is plenty of time for the user to type their password +
 * pick a merchant. Beyond that we fall back to the home page so the
 * landing experience after a long pause feels intentional, not "where
 * was I again?".
 */
const RETURN_TO_TTL_MS = 5 * 60 * 1000;

class SessionHelper {
  private cache: Record<string, unknown> = {};

  // ── Storage backend ─────────────────────────────────────────────────────────

  private storage(): Storage {
    return this.getRememberMe() ? window.localStorage : window.sessionStorage;
  }

  private getRaw(key: string): string | null {
    if (key in this.cache && typeof this.cache[key] === 'string') {
      return this.cache[key] as string;
    }
    const value = this.storage().getItem(key);
    if (value !== null) this.cache[key] = value;
    return value;
  }

  private setRaw(key: string, value: string): void {
    this.storage().setItem(key, value);
    this.cache[key] = value;
  }

  private remove(key: string): void {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
    delete this.cache[key];
  }

  private encode<T>(value: T): string {
    return Base64.encode(JSON.stringify(value));
  }

  private decode<T>(value: string | null, fallback: T): T {
    if (!value) return fallback;
    try {
      return JSON.parse(Base64.decode(value)) as T;
    } catch {
      return fallback;
    }
  }

  // ── Remember-me ─────────────────────────────────────────────────────────────

  saveRememberMe(rememberMe: boolean): void {
    window.localStorage.setItem(KEYS.rememberMe, this.encode(rememberMe));
    this.cache[KEYS.rememberMe] = rememberMe;
  }

  getRememberMe(): boolean {
    if (KEYS.rememberMe in this.cache) {
      return this.cache[KEYS.rememberMe] as boolean;
    }
    const raw = window.localStorage.getItem(KEYS.rememberMe);
    const result = this.decode<boolean>(raw, false);
    this.cache[KEYS.rememberMe] = result;
    return result;
  }

  // ── Tokens ──────────────────────────────────────────────────────────────────

  saveToken(token: string): void {
    this.setRaw(KEYS.token, token);
  }
  getToken(): string | null {
    return this.getRaw(KEYS.token);
  }

  saveRefreshToken(token: string): void {
    this.setRaw(KEYS.refresh, token);
  }
  getRefreshToken(): string | null {
    return this.getRaw(KEYS.refresh);
  }

  // ── User ────────────────────────────────────────────────────────────────────

  saveUser(user: LoggedInUser): void {
    this.setRaw(KEYS.user, this.encode(user));
  }
  getUser(): LoggedInUser | null {
    const raw = this.getRaw(KEYS.user);
    return this.decode<LoggedInUser | null>(raw, null);
  }

  // ── Selected merchant ───────────────────────────────────────────────────────
  //
  // Selected merchant is a UX preference, not security state. Persist
  // unconditionally to localStorage so it survives a tab close even when
  // the user did NOT tick "Remember me." Logout still wipes it via
  // clear() below, which removes the key from BOTH storages.

  saveSelectedMerchantId(id: number | null): void {
    if (id == null) {
      this.remove(KEYS.selectedMerchantId);
    } else {
      try {
        window.localStorage.setItem(KEYS.selectedMerchantId, String(id));
        this.cache[KEYS.selectedMerchantId] = String(id);
      } catch {
        // localStorage can throw in private mode / quota exceeded — fall
        // back to sessionStorage (still better than dropping the value).
        window.sessionStorage.setItem(KEYS.selectedMerchantId, String(id));
      }
    }
  }
  getSelectedMerchantId(): number | null {
    if (KEYS.selectedMerchantId in this.cache) {
      const cached = this.cache[KEYS.selectedMerchantId];
      return typeof cached === 'string' && cached ? Number(cached) : null;
    }
    // Read localStorage first (the persistent home), then sessionStorage
    // as a fallback for private-mode tabs or older sessions that wrote
    // there before this change shipped.
    const raw =
      window.localStorage.getItem(KEYS.selectedMerchantId) ??
      window.sessionStorage.getItem(KEYS.selectedMerchantId);
    if (raw) this.cache[KEYS.selectedMerchantId] = raw;
    return raw ? Number(raw) : null;
  }

  // ── Return-to (post-session-expiry restoration) ────────────────────────────
  //
  // Stamped by the API client right before it forces a /login redirect on
  // an unrecoverable 401. Read by LoginPage on mount; consumed (cleared)
  // on successful sign-in so the second login of the same tab doesn't
  // reuse a stale path.
  //
  // Always uses sessionStorage (not localStorage) — we don't want a
  // returnTo that was set hours ago in another tab to take over a fresh
  // login. Writes are wrapped in try/catch because sessionStorage can
  // throw under quota / private mode.

  saveReturnTo(path: string): void {
    if (!path || path === '/login') return;
    try {
      window.sessionStorage.setItem(
        KEYS.returnTo,
        JSON.stringify({ path, ts: Date.now() }),
      );
    } catch {
      /* private mode / quota — silently degrade; user lands on / */
    }
  }

  /**
   * Returns the pending return-to path if one was stamped within
   * RETURN_TO_TTL_MS, or null. Does NOT clear the entry — call
   * clearReturnTo() after the navigation lands.
   */
  getReturnTo(): string | null {
    try {
      const raw = window.sessionStorage.getItem(KEYS.returnTo);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { path?: string; ts?: number };
      if (!parsed?.path || typeof parsed.ts !== 'number') return null;
      if (Date.now() - parsed.ts > RETURN_TO_TTL_MS) {
        window.sessionStorage.removeItem(KEYS.returnTo);
        return null;
      }
      return parsed.path;
    } catch {
      return null;
    }
  }

  clearReturnTo(): void {
    try { window.sessionStorage.removeItem(KEYS.returnTo); } catch { /* noop */ }
  }

  // ── Bulk ────────────────────────────────────────────────────────────────────

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Clear auth credentials. Preserves UX preferences that should survive
   * logout / session expiry — most importantly the user's last-selected
   * merchant. Without this carve-out, the picker resets to the first
   * merchant in the list on every token refresh failure, which the user
   * has flagged repeatedly.
   * <para>
   * If you genuinely need to wipe everything (account switch, sign-out
   * from a shared device), call <c>clearCompletely()</c>.
   * </para>
   */
  clear(): void {
    const preserved: Record<string, string> = {};
    const survives = [KEYS.selectedMerchantId];
    for (const key of survives) {
      const value = window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
      if (value !== null) preserved[key] = value;
    }
    Object.values(KEYS).forEach((k) => this.remove(k));
    this.cache = {};
    // Restore preserved keys directly to localStorage (the merchant id is a
    // long-lived cross-session preference; localStorage is its natural home).
    for (const [k, v] of Object.entries(preserved)) {
      try {
        window.localStorage.setItem(k, v);
        this.cache[k] = v;
      } catch {
        /* private mode / quota — fall through, picker will re-default */
      }
    }
  }

  /** Hard reset — also wipes UX preferences. Use for explicit account-switch. */
  clearCompletely(): void {
    Object.values(KEYS).forEach((k) => this.remove(k));
    this.cache = {};
  }
}

// Singleton — there's only one session per tab.
export const sessionHelper = new SessionHelper();
